export function balanceNumber(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function bankingSummary(connections, treasury) {
  const mercury = connections.map(connection => {
    const accounts = (connection.accounts || []).filter(a => a.status === "active");
    const balances = accounts.map(a => balanceNumber(a.available_balance ?? a.current_balance));
    const complete = connection.connected !== false && balances.every(b => b !== null);
    const operatingAccounts = accounts.filter(a => String(a.account_number_last4 || "") === "6816");
    const operatingBalance = operatingAccounts.length === 1 && connection.connected !== false
      ? balanceNumber(operatingAccounts[0].available_balance ?? operatingAccounts[0].current_balance) : null;
    return { label: connection.label || "Unnamed connection", currency: "USD",
      operatingBalance,
      amount: complete ? balances.reduce((sum, b) => sum + b, 0) : null,
      detail: complete ? `${accounts.length} active account${accounts.length === 1 ? "" : "s"}` : "Connection or balance unavailable" };
  });
  const groups = [{ name: "Mercury", source: "API balances · active accounts", rows: mercury }];
  const recorded = new Map();
  for (const account of treasury?.bank_accounts || []) {
    const bank = account.institution || "Other bank";
    if (!recorded.has(bank)) recorded.set(bank, []);
    recorded.get(bank).push({ label: account.account, amount: balanceNumber(account.balance),
      currency: account.currency || "USD", detail: account.as_of ? `As of ${account.as_of}` : "Balance date unavailable" });
  }
  for (const account of treasury?.bank_balances_stale || []) {
    const bank = account.institution || "Other bank";
    if (!recorded.has(bank)) recorded.set(bank, []);
    recorded.get(bank).push({ label: account.account, amount: null, currency: "USD", detail: "No balance recorded · excluded" });
  }
  for (const [name, rows] of recorded) groups.push({ name, source: "Last recorded balances", rows });
  for (const group of groups) {
    group.total = group.rows.length && group.rows.every(row => row.amount !== null && row.currency === "USD")
      ? group.rows.reduce((sum, row) => sum + row.amount, 0) : null;
  }
  const complete = treasury != null && groups.every(group => group.total !== null);
  return { groups, complete, sourcesAvailable: treasury != null && connections.length > 0, mercuryTotal: groups[0].total,
    total: complete ? groups.reduce((sum, group) => sum + group.total, 0) : null };
}

// Brian's dashboard grouping: HGIO / HGIO1 and HSS1 are fund cash.
// Unknown names stay unassigned rather than silently becoming management cash.
export function bankLevel(bank, label) {
  const key = String(label || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (bank.toLowerCase() === "mercury") {
    if (["hgio", "hgio1", "helenaglobalinvestmentopportunities1ltd", "helenaglobalinvestmentsopportunities1ltd"].includes(key)) return "fund";
    if (key === "helenapartnersinc") return "management";
    if (key === "helenapartnersllc") return "excluded";
  }
  if (bank.toLowerCase() === "citi") {
    if (["hgio", "hgio1", "hss1"].includes(key)) return "fund";
    if (key === "hpi") return "management";
  }
  return null;
}

export function bankingAtLevel(summary, level) {
  const unassigned = summary.groups.flatMap(group => group.rows
    .filter(row => !bankLevel(group.name, row.label)).map(row => `${group.name} ${row.label}`));
  const groups = summary.groups.map(group => {
    const rows = group.rows.filter(row => bankLevel(group.name, row.label) === level).map(row => {
      if (group.name.toLowerCase() !== "mercury" || level !== "management") return row;
      return { ...row, amount: row.operatingBalance,
        detail: row.operatingBalance === null ? "Account ending 6816 · balance unavailable" : "Account ending 6816 · active account" };
    });
    return { ...group, rows, total: rows.every(row => row.amount !== null && row.currency === "USD")
      ? rows.reduce((sum, row) => sum + row.amount, 0) : null };
  }).filter(group => group.rows.length);
  const complete = summary.sourcesAvailable && groups.length > 0 && unassigned.length === 0 && groups.every(group => group.total !== null);
  return { groups, complete, unassigned, total: complete ? groups.reduce((sum, group) => sum + group.total, 0) : null };
}
